import { compressSync, decompressSync, strFromU8, strToU8 } from 'fflate';
import { photoLibraryCache } from './photoLibraryCache';

const removeCanvas = key => {
  localStorage.removeItem(key);
};
const loadCanvas = key => {
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};
const saveCanvas = (key, { outlay, inlay_0, inlay_1 }) => {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        outlay,
        inlay_0,
        inlay_1,
      })
    );
  } catch (error) {
    // ignore oversized canvas payloads
  }
};
const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};
const saveUser = user => {
  localStorage.setItem('user', JSON.stringify(user));
};
const removeUser = () => {
  localStorage.removeItem('user');
};
const saveDesignId = id => {
  localStorage.setItem('design-id', id);
};
const getDesignId = () => {
  return localStorage.getItem('design-id');
};
const clearDesignId = () => {
  localStorage.removeItem('design-id');
};

const LIBRARY_CACHE_KEY = 'photo-library-cache';
export const LIBRARY_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const MOBILE_UPLOAD_SESSION_KEY = 'mobile-upload-session';
const MOBILE_UPLOAD_DEVICE_KEY = 'mobile-upload-device-id';
const MOBILE_UPLOAD_SESSION_TTL = LIBRARY_CACHE_TTL;
const MAX_INLINE_DATA_URL_LENGTH = 3 * 1024 * 1024; // ~3MB guard for localStorage
const DATA_PAYLOAD_VERSION = 1;
const INITIAL_HYDRATE_COUNT = 12;
const WORKFLOW_CACHE_PREFIX = 'workflow-cache::';
const WORKFLOW_CACHE_INTENT_KEY = 'workflow-cache-intent';
const WORKFLOW_RETURN_PATH_PREFIX = 'workflow-return-path::';
const WORKFLOW_LAST_LISTING_PATH_KEY = 'workflow-last-listing-path';
const WORKFLOW_CACHE_VERSION = 1;
const WORKFLOW_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
const WORKFLOW_INTENT_TTL = 10 * 60 * 1000; // 10 minutes
const DEFAULT_SLUG_ORIGIN = 'https://workflow.local';
const EMBEDDED_PHOTO_PREFIX = 'workflow-embedded-photo::';
const isQuotaLikeError = error =>
  error &&
  (error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_FILE_NO_DEVICE_SPACE' ||
    error.name === 'AbortError');

const extractBase64Components = dataUrl => {
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
    return null;
  }
  const match = dataUrl.match(/^data:([^;]+);base64,(.*)$/s);
  if (!match) return null;
  const mimeType = match[1] || 'image/jpeg';
  const base64 = match[2];
  return { mimeType, base64 };
};

const encodeDataPayload = dataUrl => {
  const parts = extractBase64Components(dataUrl);
  if (!parts) return null;
  try {
    const compressed = compressSync(strToU8(parts.base64), { level: 6 });
    return {
      version: DATA_PAYLOAD_VERSION,
      mimeType: parts.mimeType,
      data: compressed,
      originalLength: parts.base64.length,
    };
  } catch (error) {
    return null;
  }
};

const decodeDataPayload = payload => {
  if (!payload) return null;
  if (typeof payload === 'string') {
    return payload.startsWith('data:') ? payload : null;
  }
  if (payload instanceof Uint8Array) {
    try {
      const base64 = strFromU8(decompressSync(payload));
      return `data:image/jpeg;base64,${base64}`;
    } catch (error) {
      return null;
    }
  }
  if (typeof payload !== 'object') {
    return null;
  }
  try {
    if (payload.version === DATA_PAYLOAD_VERSION && payload.data) {
      const base64 = strFromU8(decompressSync(payload.data));
      const mimeType = payload.mimeType || 'image/jpeg';
      return `data:${mimeType};base64,${base64}`;
    }
    if (payload.data) {
      const base64 = strFromU8(decompressSync(payload.data));
      const mimeType = payload.mimeType || 'image/jpeg';
      return `data:${mimeType};base64,${base64}`;
    }
  } catch (error) {
    return null;
  }
  return null;
};

const pickRemoteUrl = (...candidates) =>
  candidates.find(
    value => typeof value === 'string' && value.trim() && !value.startsWith('data:')
  ) || null;

const dataUrlOrNull = value =>
  typeof value === 'string' && value.startsWith('data:') ? value : null;

const serializeLibraryPhoto = photo => {
  if (!photo || typeof photo !== 'object') return null;
  if (photo.isPending || photo.isError) return null;
  const id = photo.id || photo.remotePhotoId;
  if (!id) return null;

  const primaryUrl =
    photo.url || photo.originalUrl || photo.preview || photo.thumbnailUrl || null;
  if (
    !primaryUrl &&
    !dataUrlOrNull(photo.cachedDataUrl) &&
    !dataUrlOrNull(photo.preview) &&
    !dataUrlOrNull(photo.thumbnailUrl)
  ) {
    return null;
  }

  const sourceDataUrl =
    dataUrlOrNull(photo.cachedDataUrl) ||
    dataUrlOrNull(primaryUrl) ||
    dataUrlOrNull(photo.preview) ||
    dataUrlOrNull(photo.thumbnailUrl);

  const remoteUrl = pickRemoteUrl(
    photo.remoteUrl,
    photo.originalRemoteUrl,
    photo.originalUrl,
    photo.url
  );

  const remotePreview = pickRemoteUrl(
    photo.preview,
    photo.thumbnailUrl,
    photo.previewUrl,
    remoteUrl
  );

  const sessionId = photo.sessionId || photo.cachedSessionId || null;
  const remotePhotoId = photo.remotePhotoId || photo.cachedRemotePhotoId || null;
  const cacheKey = sourceDataUrl ? String(id) : null;
  const allowLargeInline = !photoLibraryCache.isPersistent();
  const inlineDataUrl =
    sourceDataUrl &&
    (allowLargeInline || sourceDataUrl.length <= MAX_INLINE_DATA_URL_LENGTH)
      ? sourceDataUrl
      : null;

  return {
    id,
    fileName: photo.fileName || null,
    source: photo.source || null,
    mimeType: photo.mimeType || null,
    cachedAt: photo.cachedAt || Date.now(),
    pinned: Boolean(photo.pinned),
    sessionId,
    cachedSessionId: photo.cachedSessionId || sessionId,
    cachedRemotePhotoId: photo.cachedRemotePhotoId || remotePhotoId,
    remotePhotoId: remotePhotoId,
    dataCacheKey: cacheKey,
    hasCachedData: Boolean(cacheKey),
    cachedDataUrl: inlineDataUrl,
    url: remoteUrl,
    originalUrl: remoteUrl,
    preview: remotePreview,
    thumbnailUrl: remotePreview,
    remoteUrl: remoteUrl,
    originalRemoteUrl: photo.originalRemoteUrl || null,
  };
};

const hydratePhotoWithData = (photo, dataUrl) => {
  if (!photo || !dataUrl) return photo;
  const hydrated = {
    ...photo,
    cachedDataUrl: dataUrl,
    url: dataUrl,
    originalUrl: dataUrl,
  };
  if (!hydrated.preview || hydrated.preview.startsWith('data:')) {
    hydrated.preview = dataUrl;
  }
  if (!hydrated.thumbnailUrl || hydrated.thumbnailUrl.startsWith('data:')) {
    hydrated.thumbnailUrl = dataUrl;
  }
  hydrated.hasCachedData = true;
  return hydrated;
};

const loadLibraryPhotos = async () => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = localStorage.getItem(LIBRARY_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();

    const candidates = [];
    const hydrationTargets = [];
    const expiredCacheKeys = [];

  parsed.forEach(item => {
      if (!item || typeof item !== 'object' || !item.id) {
        return;
      }
      const cachedAt = item.cachedAt || now;
      if (now - cachedAt >= LIBRARY_CACHE_TTL) {
        if (item.dataCacheKey) {
          expiredCacheKeys.push(item.dataCacheKey);
        }
        return;
      }
      const normalized = {
        ...item,
        cachedAt,
        pinned: Boolean(item.pinned),
        cachedDataUrl:
          dataUrlOrNull(item.cachedDataUrl) ||
          dataUrlOrNull(item.url) ||
          dataUrlOrNull(item.originalUrl) ||
          dataUrlOrNull(item.preview) ||
          dataUrlOrNull(item.thumbnailUrl) ||
          null,
      };
      // If we have any URL source, ensure it's not shown as loading
      const hasAnyUrl = Boolean(
        (typeof item.url === 'string' && item.url) ||
          (typeof item.originalUrl === 'string' && item.originalUrl) ||
          (typeof item.preview === 'string' && item.preview) ||
          (typeof item.thumbnailUrl === 'string' && item.thumbnailUrl)
      );
      if (hasAnyUrl) {
        normalized.isLoading = false;
        normalized.isPending = false;
        normalized.progress = 100;
      }
      if (!normalized.dataCacheKey && normalized.cachedDataUrl && item.id) {
        normalized.dataCacheKey = String(item.id);
      }
      if (normalized.cachedDataUrl) {
        normalized.url = normalized.cachedDataUrl;
        normalized.originalUrl = normalized.cachedDataUrl;
        if (!normalized.preview || dataUrlOrNull(normalized.preview)) {
          normalized.preview = normalized.cachedDataUrl;
        }
        if (!normalized.thumbnailUrl || dataUrlOrNull(normalized.thumbnailUrl)) {
          normalized.thumbnailUrl = normalized.cachedDataUrl;
        }
        normalized.hasCachedData = true;
      }
      candidates.push(normalized);
      if (item.dataCacheKey) {
        hydrationTargets.push({
          cacheKey: item.dataCacheKey,
          target: normalized,
          pinned: Boolean(item.pinned),
        });
      }
    });

    // Prioritize pinned entries so placed photos are hydrated first
    const sortedTargets = hydrationTargets.sort((a, b) => {
      const ap = a.pinned ? 1 : 0;
      const bp = b.pinned ? 1 : 0;
      if (ap !== bp) return bp - ap; // pinned first
      // older first to keep behavior stable
      const at = a.target?.cachedAt || 0;
      const bt = b.target?.cachedAt || 0;
      return at - bt;
    });
    const immediateTargets = sortedTargets.slice(0, INITIAL_HYDRATE_COUNT);
    for (const job of immediateTargets) {
      try {
        const storedPayload = await photoLibraryCache.get(job.cacheKey);
        const dataUrl = decodeDataPayload(storedPayload);
        if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
          const hydrated = hydratePhotoWithData(job.target, dataUrl);
          Object.assign(job.target, hydrated);
        } else {
          job.target.hasCachedData = false;
          job.target.dataCacheKey = null;
        }
      } catch (error) {
        job.target.hasCachedData = false;
        job.target.dataCacheKey = null;
      }
    }

    const pendingHydration = sortedTargets.slice(INITIAL_HYDRATE_COUNT).map(job => ({
      cacheKey: job.cacheKey,
      id: job.target?.id || null,
    }));

    if (expiredCacheKeys.length) {
      await photoLibraryCache.deleteMany(expiredCacheKeys);
    }

    // Sanitize: drop zombies (no dataCacheKey and no URL sources) if not pinned
    const before = candidates.length;
    const sanitized = candidates.filter(it => {
      const hasUrlSource = Boolean(
        (typeof it.url === 'string' && it.url) ||
          (typeof it.originalUrl === 'string' && it.originalUrl) ||
          (typeof it.preview === 'string' && it.preview) ||
          (typeof it.thumbnailUrl === 'string' && it.thumbnailUrl)
      );
      if (it.pinned) return true;
      if (!it.dataCacheKey && !hasUrlSource) return false;
      return true;
    });
    const after = sanitized.length;
    if (process.env.NODE_ENV !== 'production') {
      try {
        console.log('[library] sanitized', { dropped: before - after, kept: after, pinned: sanitized.filter(i => i.pinned).length });
      } catch (e) {}
    }

    return {
      items: sanitized,
      hydrationQueue: pendingHydration,
    };
  } catch (error) {
    return {
      items: [],
      hydrationQueue: [],
    };
  }
};

const loadLibraryPhotoData = async cacheKey => {
  if (!cacheKey) return null;
  try {
    const payload = await photoLibraryCache.get(cacheKey);
    return decodeDataPayload(payload);
  } catch (error) {
    return null;
  }
};

const saveLibraryPhotos = async photos => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (!Array.isArray(photos)) return;
    const serialized = [];
    const dataEntries = [];
    const dataCacheValues = new Map();

    photos.forEach(photo => {
      const serializedPhoto = serializeLibraryPhoto(photo);
      if (!serializedPhoto) return;

      const cachedAt = serializedPhoto.cachedAt || Date.now();
      serializedPhoto.cachedAt = cachedAt;

      const dataUrlForCache =
        dataUrlOrNull(photo.cachedDataUrl) ||
        dataUrlOrNull(photo.url) ||
        dataUrlOrNull(photo.originalUrl) ||
        dataUrlOrNull(photo.preview) ||
        dataUrlOrNull(photo.thumbnailUrl);

      // Guarantee an IDB payload key if we have any data URL, even when
      // the serialized entry did not include one (e.g., inline removed
      // to keep localStorage small). This ensures desktop uploads always
      // have a resolvable payload on resume.
      if (dataUrlForCache && !serializedPhoto.dataCacheKey) {
        try {
          const keyId = String(serializedPhoto.id || photo.id || photo.remotePhotoId || photo.cachedRemotePhotoId || '');
          if (keyId) serializedPhoto.dataCacheKey = keyId;
        } catch (e) {}
      }

      if (serializedPhoto.dataCacheKey && dataUrlForCache) {
        const payload = encodeDataPayload(dataUrlForCache);
        if (payload) {
          dataEntries.push({ key: serializedPhoto.dataCacheKey, value: payload });
          dataCacheValues.set(serializedPhoto.dataCacheKey, dataUrlForCache);
          if (!photoLibraryCache.isPersistent()) {
            serializedPhoto.cachedDataUrl = dataUrlForCache;
            serializedPhoto.hasCachedData = true;
            serializedPhoto.dataCacheKey = null;
          } else {
            serializedPhoto.cachedDataUrl = null;
          }
        } else {
          serializedPhoto.cachedDataUrl = dataUrlForCache;
          serializedPhoto.hasCachedData = true;
          serializedPhoto.dataCacheKey = null;
        }
      } else if (dataUrlForCache && !serializedPhoto.cachedDataUrl) {
        serializedPhoto.cachedDataUrl = dataUrlForCache;
        serializedPhoto.hasCachedData = true;
      }

      serialized.push(serializedPhoto);
    });

    let storedKeys = [];
    let failedKeys = [];

    if (dataEntries.length && photoLibraryCache.isPersistent()) {
      const result = await photoLibraryCache.bulkSet(dataEntries);
      storedKeys = Array.isArray(result?.stored) ? result.stored : [];
      failedKeys = Array.isArray(result?.failed) ? result.failed : [];

      if (failedKeys.length) {
        const failedSet = new Set(failedKeys);
        serialized.forEach(item => {
          if (!item || !item.dataCacheKey) return;
          if (!failedSet.has(item.dataCacheKey)) return;
          const fallbackData = dataCacheValues.get(item.dataCacheKey);
          if (fallbackData) {
            item.cachedDataUrl = fallbackData;
            item.hasCachedData = true;
            item.url = fallbackData;
            item.originalUrl = fallbackData;
            item.preview = fallbackData;
            item.thumbnailUrl = fallbackData;
          }
          item.dataCacheKey = null;
        });
        storedKeys = storedKeys.filter(key => !failedSet.has(key));
      }
    } else if (!photoLibraryCache.isPersistent()) {
      storedKeys = [];
      failedKeys = dataEntries.map(entry => entry.key);
    }

    const storedKeySet = new Set(storedKeys);
    const removedCacheKeys = [];

    const removeOldestSerializedEntry = () => {
      if (!serialized.length) {
        return null;
      }
      // Prefer removing non-pinned entries first, oldest wins.
      let oldestIndex = -1;
      let oldestTimestamp = Number.POSITIVE_INFINITY;
      for (let idx = 0; idx < serialized.length; idx += 1) {
        const candidate = serialized[idx];
        if (candidate && candidate.pinned) continue; // skip pinned
        const candidateTs = typeof candidate?.cachedAt === 'number' ? candidate.cachedAt : 0;
        if (candidateTs < oldestTimestamp) {
          oldestTimestamp = candidateTs;
          oldestIndex = idx;
        }
      }
      // If everything is pinned, fall back to removing the absolute oldest
      if (oldestIndex === -1) {
        oldestIndex = 0;
        oldestTimestamp = typeof serialized[0]?.cachedAt === 'number' ? serialized[0].cachedAt : 0;
        for (let idx = 1; idx < serialized.length; idx += 1) {
          const candidate = serialized[idx];
          const candidateTs = typeof candidate?.cachedAt === 'number' ? candidate.cachedAt : 0;
          if (candidateTs < oldestTimestamp) {
            oldestTimestamp = candidateTs;
            oldestIndex = idx;
          }
        }
      }
      return serialized.splice(oldestIndex, 1)[0];
    };

    let didPersist = false;
    while (!didPersist) {
      try {
        localStorage.setItem(LIBRARY_CACHE_KEY, JSON.stringify(serialized));
        didPersist = true;
      } catch (error) {
        if (!isQuotaLikeError(error)) {
          throw error;
        }
        if (!serialized.length) {
          try {
            localStorage.removeItem(LIBRARY_CACHE_KEY);
          } catch (removeError) {
            // ignore secondary errors
          }
          break;
        }
        const removed = removeOldestSerializedEntry();
        if (removed && removed.dataCacheKey) {
          storedKeySet.delete(removed.dataCacheKey);
          removedCacheKeys.push(removed.dataCacheKey);
        }
      }
    }

    if (removedCacheKeys.length) {
      await photoLibraryCache.deleteMany(removedCacheKeys);
    }

    if (storedKeySet.size) {
      await photoLibraryCache.removeAllExcept(Array.from(storedKeySet));
    } else {
      await photoLibraryCache.clear();
    }
  } catch (error) {
    // ignore storage errors
  }
};

const clearLibraryPhotos = async () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(LIBRARY_CACHE_KEY);
  } catch (error) {
    // ignore
  }
  await photoLibraryCache.clear();
};

const generateDeviceId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getOrCreateMobileUploadDeviceId = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    let existing = localStorage.getItem(MOBILE_UPLOAD_DEVICE_KEY);
    if (existing && typeof existing === 'string') {
      return existing;
    }
    const deviceId = generateDeviceId();
    localStorage.setItem(MOBILE_UPLOAD_DEVICE_KEY, deviceId);
    return deviceId;
  } catch (error) {
    return null;
  }
};

const saveMobileUploadSession = (session, ownerId = null) => {
  if (!session || typeof session !== 'object') return;
  if (!session.sessionId) return;
  try {
    const payload = {
      ...session,
      cachedAt: Date.now(),
      ownerId: session.ownerId || ownerId || null,
    };
    localStorage.setItem(MOBILE_UPLOAD_SESSION_KEY, JSON.stringify(payload));
  } catch (error) {
    // ignore storage errors
  }
};

const loadMobileUploadSession = (expectedOwnerId = null) => {
  try {
    const raw = localStorage.getItem(MOBILE_UPLOAD_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.sessionId) return null;
    const cachedAt = parsed.cachedAt || 0;
    if (cachedAt) {
      const now = Date.now();
      if (now - cachedAt >= MOBILE_UPLOAD_SESSION_TTL) {
        localStorage.removeItem(MOBILE_UPLOAD_SESSION_KEY);
        return null;
      }
    }
    if (
      expectedOwnerId &&
      parsed.ownerId &&
      parsed.ownerId !== expectedOwnerId
    ) {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

const clearMobileUploadSession = () => {
  try {
    localStorage.removeItem(MOBILE_UPLOAD_SESSION_KEY);
  } catch (error) {
    // ignore
  }
};

const buildEmbeddedPhotoKey = cacheKey =>
  cacheKey ? `${EMBEDDED_PHOTO_PREFIX}${cacheKey}` : null;

const saveEmbeddedPhotoData = (cacheKey, dataUrl) => {
  if (typeof window === 'undefined' || !cacheKey || !dataUrl) {
    return null;
  }
  try {
    const key = buildEmbeddedPhotoKey(cacheKey);
    if (!key) {
      return null;
    }
    localStorage.setItem(key, dataUrl);
    return cacheKey;
  } catch (error) {
    return null;
  }
};

const loadEmbeddedPhotoData = cacheKey => {
  if (typeof window === 'undefined' || !cacheKey) {
    return null;
  }
  try {
    const key = buildEmbeddedPhotoKey(cacheKey);
    if (!key) {
      return null;
    }
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

export const normalizeWorkflowSlug = slug => {
  if (slug == null) {
    return null;
  }
  let candidate =
    typeof slug === 'string' ? slug.trim() : String(slug).trim();
  if (!candidate) {
    return null;
  }
  const hasProtocol = /^[a-z]+:\/\//i.test(candidate);
  if (!hasProtocol && candidate.startsWith('//')) {
    candidate = `https:${candidate}`;
  }
  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : DEFAULT_SLUG_ORIGIN;
    const url = new URL(candidate, origin);
    candidate = url.pathname || candidate;
  } catch (error) {
    const sanitized = candidate.replace(/^[a-z]+:\/\//i, '');
    const slashIndex = sanitized.indexOf('/');
    candidate =
      slashIndex >= 0 ? sanitized.slice(slashIndex) : `/${sanitized}`;
  }
  try {
    candidate = decodeURIComponent(candidate);
  } catch (error) {
    // ignore malformed sequences
  }
  candidate = candidate.replace(/\/{2,}/g, '/');
  candidate = candidate.replace(/[#?].*$/, '');
  if (!candidate.startsWith('/')) {
    candidate = `/${candidate}`;
  }
  candidate = candidate.replace(/\/+$/, '');
  if (!candidate) {
    candidate = '/';
  }
  return candidate.toLowerCase();
};

const buildWorkflowCacheKey = slug => {
  const normalized = normalizeWorkflowSlug(slug);
  if (!normalized) return null;
  return `${WORKFLOW_CACHE_PREFIX}${normalized}`;
};

const parseWorkflowCacheEntry = (raw, key = null) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== WORKFLOW_CACHE_VERSION) {
      if (key) localStorage.removeItem(key);
      return null;
    }
    if (
      parsed.savedAt &&
      Date.now() - parsed.savedAt > WORKFLOW_CACHE_TTL
    ) {
      if (key) localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch (error) {
    if (key) localStorage.removeItem(key);
    return null;
  }
};

const listWorkflowCacheKeys = () => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return [];
  }
  const keys = [];
  try {
    for (let idx = 0; idx < localStorage.length; idx += 1) {
      const key = localStorage.key(idx);
      if (key && key.startsWith(WORKFLOW_CACHE_PREFIX)) {
        keys.push(key);
      }
    }
  } catch (error) {
    return keys;
  }
  return keys;
};

const pruneWorkflowCacheEntries = (options = {}) => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return false;
  }
  const { keepSlug = null, maxRemovals = 3 } = options;
  const normalizedKeep = normalizeWorkflowSlug(keepSlug);
  const keys = listWorkflowCacheKeys();
  if (!keys.length) {
    return false;
  }
  const candidates = [];
  keys.forEach(key => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return;
      }
      const parsed = parseWorkflowCacheEntry(raw);
      if (!parsed || !parsed.slug) {
        localStorage.removeItem(key);
        return;
      }
      const normalizedSlug = normalizeWorkflowSlug(parsed.slug);
      if (normalizedKeep && normalizedSlug === normalizedKeep) {
        return;
      }
      candidates.push({
        key,
        savedAt: parsed.savedAt || 0,
      });
    } catch (error) {
      localStorage.removeItem(key);
    }
  });
  if (!candidates.length) {
    return false;
  }
  candidates.sort((a, b) => (a.savedAt || 0) - (b.savedAt || 0));
  const removals = Math.min(maxRemovals, candidates.length);
  for (let idx = 0; idx < removals; idx += 1) {
    localStorage.removeItem(candidates[idx].key);
  }
  return true;
};

const saveWorkflowCacheEntry = (slug, payload = {}) => {
  if (typeof window === 'undefined') return null;
  const normalizedSlug = normalizeWorkflowSlug(slug);
  const key = buildWorkflowCacheKey(normalizedSlug);
  if (!key) return null;
  try {
    const entry = {
      version: WORKFLOW_CACHE_VERSION,
      savedAt: payload.savedAt || Date.now(),
      slug: normalizedSlug,
      ...payload,
    };
    localStorage.setItem(key, JSON.stringify(entry));
    return entry;
  } catch (error) {
    if (!isQuotaLikeError(error)) {
      return null;
    }
    const pruned = pruneWorkflowCacheEntries({ keepSlug: normalizedSlug, maxRemovals: 2 });
    if (!pruned) {
      return null;
    }
    try {
      const entry = {
        version: WORKFLOW_CACHE_VERSION,
        savedAt: payload.savedAt || Date.now(),
        slug: normalizedSlug,
        ...payload,
      };
      localStorage.setItem(key, JSON.stringify(entry));
      return entry;
    } catch (retryError) {
      return null;
    }
  }
};

const loadWorkflowCacheEntry = slug => {
  if (typeof window === 'undefined') return null;
  const key = buildWorkflowCacheKey(slug);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return parseWorkflowCacheEntry(raw, key);
  } catch (error) {
    localStorage.removeItem(key);
    return null;
  }
};

const clearWorkflowCacheEntry = slug => {
  if (typeof window === 'undefined') return;
  const key = buildWorkflowCacheKey(slug);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // ignore
  }
};

const saveWorkflowRestoreIntent = intent => {
  if (typeof window === 'undefined') return;
  if (!intent || !intent.slug) {
    localStorage.removeItem(WORKFLOW_CACHE_INTENT_KEY);
    return;
  }
  try {
    const normalizedSlug = normalizeWorkflowSlug(intent.slug);
    if (!normalizedSlug) {
      localStorage.removeItem(WORKFLOW_CACHE_INTENT_KEY);
      return;
    }
    const payload = {
      slug: normalizedSlug,
      action: intent.action || 'continue',
      createdAt: Date.now(),
    };
    localStorage.setItem(WORKFLOW_CACHE_INTENT_KEY, JSON.stringify(payload));
  } catch (error) {
    // ignore
  }
};

const consumeWorkflowRestoreIntent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(WORKFLOW_CACHE_INTENT_KEY);
    if (!raw) return null;
    localStorage.removeItem(WORKFLOW_CACHE_INTENT_KEY);
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      (!parsed.createdAt ||
        Date.now() - parsed.createdAt <= WORKFLOW_INTENT_TTL)
    ) {
      return parsed;
    }
    return null;
  } catch (error) {
    localStorage.removeItem(WORKFLOW_CACHE_INTENT_KEY);
    return null;
  }
};

const findWorkflowCacheEntry = slug => {
  if (typeof window === 'undefined') return null;
  const normalizedTarget = normalizeWorkflowSlug(slug);
  if (normalizedTarget) {
    const direct = loadWorkflowCacheEntry(normalizedTarget);
    if (direct) {
      return direct;
    }
  }
  const keys = listWorkflowCacheKeys();
  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key);
      const parsed = parseWorkflowCacheEntry(raw, key);
      if (!parsed || !parsed.slug) {
        continue;
      }
      const entrySlug = normalizeWorkflowSlug(parsed.slug);
      if (entrySlug && entrySlug === normalizedTarget) {
        return parsed;
      }
    } catch (error) {
      // ignore malformed cache
    }
  }
  return null;
};

const getCurrentPath = () => {
  if (typeof window === 'undefined') return null;
  const { pathname, search } = window.location || {};
  if (!pathname) return null;
  return `${pathname}${search || ''}`;
};

const isWorkflowPath = path => {
  if (typeof path !== 'string') return false;
  return path
    .split('?')[0]
    .split('/')
    .filter(Boolean).length >= 3;
};

const isWorkflowListingPath = path => {
  if (typeof path !== 'string' || !path.startsWith('/')) return false;
  const pathname = path.split('?')[0].replace(/\/+$/, '') || '/';
  if (pathname === '/') return false;
  if (pathname === '/search') return true;
  if (!pathname.startsWith('/greeting-cards')) return false;
  return !isWorkflowPath(pathname);
};

const saveWorkflowLastListingPath = (path, scrollY) => {
  if (typeof window === 'undefined') return;
  const listingPath = path || getCurrentPath();
  if (!isWorkflowListingPath(listingPath)) return;
  const payload = {
    path: listingPath,
    scrollY:
      typeof scrollY === 'number' && !Number.isNaN(scrollY)
        ? scrollY
        : window.scrollY || window.pageYOffset || 0,
    updatedAt: Date.now(),
  };
  try {
    window.sessionStorage.setItem(
      WORKFLOW_LAST_LISTING_PATH_KEY,
      JSON.stringify(payload)
    );
  } catch (error) {
    // ignore private browsing / storage errors
  }
};

const loadWorkflowLastListingPath = () => {
  if (typeof window === 'undefined') return null;
  try {
    return parseWorkflowReturnState(
      window.sessionStorage.getItem(WORKFLOW_LAST_LISTING_PATH_KEY)
    );
  } catch (error) {
    return null;
  }
};

const saveWorkflowReturnPath = (slug, path) => {
  if (typeof window === 'undefined') return;
  const normalizedSlug = normalizeWorkflowSlug(slug);
  const returnPath = path || getCurrentPath();
  if (!normalizedSlug || !returnPath || returnPath === normalizedSlug) {
    return;
  }
  const scrollY = window.scrollY || window.pageYOffset || 0;
  const payload = {
    path: returnPath,
    scrollY,
    updatedAt: Date.now(),
  };
  if (isWorkflowListingPath(returnPath)) {
    saveWorkflowLastListingPath(returnPath, scrollY);
  }
  try {
    window.sessionStorage.setItem(
      `${WORKFLOW_RETURN_PATH_PREFIX}${normalizedSlug}`,
      JSON.stringify(payload)
    );
  } catch (error) {
    // ignore private browsing / storage errors
  }
};

const parseWorkflowReturnState = raw => {
  if (!raw) return null;
  if (raw.startsWith('/')) {
    return { path: raw, scrollY: null };
  }
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const path = typeof parsed.path === 'string' ? parsed.path : null;
    if (!path || !path.startsWith('/')) {
      return null;
    }
    return {
      path,
      scrollY:
        typeof parsed.scrollY === 'number' && !Number.isNaN(parsed.scrollY)
          ? parsed.scrollY
          : null,
      updatedAt:
        typeof parsed.updatedAt === 'number' ? parsed.updatedAt : null,
    };
  } catch (error) {
    return null;
  }
};

const loadWorkflowReturnState = slug => {
  if (typeof window === 'undefined') return null;
  const normalizedSlug = normalizeWorkflowSlug(slug);
  if (!normalizedSlug) return null;
  try {
    const raw = window.sessionStorage.getItem(
      `${WORKFLOW_RETURN_PATH_PREFIX}${normalizedSlug}`
    );
    return parseWorkflowReturnState(raw);
  } catch (error) {
    return null;
  }
};

const loadWorkflowReturnPath = slug => {
  const returnState = loadWorkflowReturnState(slug);
  return returnState?.path || null;
};

export const localstorageHelper = {
    getUser, // get user
    saveUser, // save user
    removeUser, // remove user

    loadCanvas, // load canvas objects
    saveCanvas,     // save canvas objects
    
    removeCanvas,  // clear front-step canvas objects

    saveDesignId, // Save the current design id
    getDesignId, // Get the current design id
    clearDesignId,

    loadLibraryPhotos,
    loadLibraryPhotoData,
    saveLibraryPhotos,
    clearLibraryPhotos,

    saveMobileUploadSession,
    loadMobileUploadSession,
    clearMobileUploadSession,
    getOrCreateMobileUploadDeviceId,
    saveWorkflowCacheEntry,
    loadWorkflowCacheEntry,
    clearWorkflowCacheEntry,
    saveWorkflowRestoreIntent,
    consumeWorkflowRestoreIntent,
    findWorkflowCacheEntry,
    normalizeWorkflowSlug,
    saveWorkflowReturnPath,
    loadWorkflowReturnState,
    loadWorkflowReturnPath,
    saveWorkflowLastListingPath,
    loadWorkflowLastListingPath,
    saveEmbeddedPhotoData,
    loadEmbeddedPhotoData,
};
